import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, ReactiveFormsModule, FormsModule, Validators } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { MembershipRoles } from '../../core/models/custom-claims.model';
import { NgxMaskDirective } from 'ngx-mask';
import { toNationalDigits } from 'src/app/shared/utils/format.utils';
import { OrganizationType } from 'src/app/core/models/organization.model';

interface Organization {
  id: string;
  name: string;
  organizationType: OrganizationType;
  streetAddressLine1: string;
  city: string;
  state: string;
  zipCode: string;
  phone?: string;
  ein?: string;
}

interface Drone {
  id: string;
  serialNumber: string;
  nickname: string;
  model: string;
  manufacturer: string;
  status: string;
}

interface AssignedDrone {
  id: string;
  serialNumber: string;
  name: string;
  modelName: string;
  manufacturerName: string | null;
  status: string;
  source: 'adn' | 'organization';
  assignedAt?: string;
}

interface Invitation {
  id: string;
  email: string;
  role: number;
  emailStatus: string;
  createdAt: string;
}

interface TeamMember {
  membershipId: string;
  profileId: string;
  email: string;
  firstName: string;
  lastName: string;
  role: number;
  combinedRole: number;
  isOwner: boolean;
  isChiefSupervisor: boolean;
  isActive: boolean;
}


/**
 * Organization Setup Wizard
 *
 * Fully skippable wizard for completing organization setup after initial creation.
 * Steps:
 * 1. Organization Details (optional: phone, tax ID, notes)
 * 2. Drone Assignment (skippable)
 * 3. Additional Team Members (skippable)
 */
@Component({
  selector: 'app-organization-setup-wizard',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, NgxMaskDirective],
  templateUrl: './organization-setup-wizard.component.html',
  styleUrl: './organization-setup-wizard.component.scss'
})
export class OrganizationSetupWizardComponent implements OnInit {
  organizationId!: string;
  organization: Organization | null = null;

  // Wizard state
  currentStep = 1;
  totalSteps = 3;

  // Loading states
  loading = true;
  saving = false;
  errorMessage = '';
  successMessage = '';

  // Step 1: Organization Details Form
  detailsForm: FormGroup;

  // Step 2: Drone Assignment
  availableDrones: Drone[] = [];
  assignedDrones: AssignedDrone[] = [];
  selectedDroneIds: string[] = [];
  loadingDrones = false;
  droneSearchQuery = '';

  // Step 3: Team Members
  teamMemberForm: FormGroup;
  existingMembers: TeamMember[] = [];
  pendingInvitations: Invitation[] = [];
  addingMember = false;
  loadingMembers = false;

  /** Available roles for selection (Owner and Chief Supervisor are singular, assigned elsewhere) */
  availableRoles = [
    { value: MembershipRoles.PilotInCharge, label: 'Pilot in Command', description: 'Can supervise flights' },
    { value: MembershipRoles.Pilot, label: 'Pilot', description: 'Can log flights' },
  ];

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private http: HttpClient
  ) {
    // Step 1: Organization Details
    this.detailsForm = this.fb.group({
      phone: ['', [Validators.pattern(/^\+?[\d\s\-()]+$/)]],
      ein: ['', [Validators.pattern(/^\d{2}-?\d{7}$/)]],
      notes: [''],
    });

    // Step 3: Team Member Form (default to Pilot role)
    this.teamMemberForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      firstName: [''],
      lastName: [''],
      role: [MembershipRoles.Pilot, Validators.required],
    });
  }

  ngOnInit(): void {
    this.organizationId = this.route.snapshot.params['id'];

    // Check for step query param
    const stepParam = this.route.snapshot.queryParams['step'];
    if (stepParam) {
      const step = parseInt(stepParam, 10);
      if (step >= 1 && step <= this.totalSteps) {
        this.currentStep = step;
      }
    }

    this.loadOrganization();
  }

  async loadOrganization(): Promise<void> {
    this.loading = true;
    this.errorMessage = '';

    try {
      const response = await firstValueFrom(
        this.http.get<{ success: boolean; data: Organization }>(
          `${environment.apiUrl}/admin/organizations/${this.organizationId}`
        )
      );

      if (response.success && response.data) {
        this.organization = response.data;

        // Pre-fill details form with existing data
        this.detailsForm.patchValue({
          phone: toNationalDigits(response.data.phone),
          ein: response.data.ein || '',
        });

        // Load data for current step
        await this.loadStepData();
      }
    } catch (error: any) {
      console.error('Failed to load organization:', error);
      this.errorMessage = 'Failed to load organization details';
    } finally {
      this.loading = false;
    }
  }

  async loadStepData(): Promise<void> {
    switch (this.currentStep) {
      case 2:
        await Promise.all([
          this.loadAvailableDrones(),
          this.loadAssignedDrones(),
        ]);
        break;
      case 3:
        await Promise.all([
          this.loadExistingMembers(),
          this.loadPendingInvitations(),
        ]);
        break;
    }
  }

  // =========================================
  // Navigation
  // =========================================

  async goToStep(step: number): Promise<void> {
    if (step < 1 || step > this.totalSteps) return;

    // Save current step before navigating
    if (this.currentStep === 1) {
      await this.saveOrganizationDetails(false);
    }

    this.currentStep = step;
    await this.loadStepData();

    // Update URL
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { step },
      queryParamsHandling: 'merge'
    });
  }

  async nextStep(): Promise<void> {
    if (this.currentStep < this.totalSteps) {
      await this.goToStep(this.currentStep + 1);
    }
  }

  async previousStep(): Promise<void> {
    if (this.currentStep > 1) {
      await this.goToStep(this.currentStep - 1);
    }
  }

  skipWizard(): void {
    this.router.navigate(['/organizations', this.organizationId]);
  }

  async finishWizard(): Promise<void> {
    // Mark organization as setup complete
    try {
      await firstValueFrom(
        this.http.put(
          `${environment.apiUrl}/admin/organizations/${this.organizationId}`,
          { setupComplete: true }
        )
      );
    } catch (error: any) {
      console.error('Failed to mark setup complete:', error);
      // Continue to navigate even if this fails
    }

    this.router.navigate(['/organizations', this.organizationId]);
  }

  // =========================================
  // Step 1: Organization Details
  // =========================================

  async saveOrganizationDetails(showSuccess = true): Promise<void> {
    if (this.detailsForm.invalid) {
      this.detailsForm.markAllAsTouched();
      return;
    }

    this.saving = true;
    this.errorMessage = '';
    this.successMessage = '';

    try {
      const formData = this.detailsForm.value;

      // Use PUT (not PATCH) as per the API
      await firstValueFrom(
        this.http.put(
          `${environment.apiUrl}/admin/organizations/${this.organizationId}`,
          {
            phone: formData.phone || null,
            ein: formData.ein || null,
          }
        )
      );

      if (showSuccess) {
        this.successMessage = 'Organization details saved';
        setTimeout(() => this.successMessage = '', 3000);
      }
    } catch (error: any) {
      console.error('Failed to save organization details:', error);
      this.errorMessage = 'Failed to save organization details';
    } finally {
      this.saving = false;
    }
  }

  // =========================================
  // Step 2: Drone Assignment
  // =========================================

  async loadAvailableDrones(): Promise<void> {
    this.loadingDrones = true;

    try {
      // Load unassigned ADN drones
      const response = await firstValueFrom(
        this.http.get<any>(`${environment.apiUrl}/admin/drones?status=available&pageSize=100`)
      );

      this.availableDrones = response.data?.drones || [];
    } catch (error: any) {
      console.error('Failed to load drones:', error);
    } finally {
      this.loadingDrones = false;
    }
  }

  async loadAssignedDrones(): Promise<void> {
    try {
      // Load drones already assigned to this organization
      const response = await firstValueFrom(
        this.http.get<any>(`${environment.apiUrl}/admin/organizations/${this.organizationId}/drones`)
      );

      // Combine ADN-assigned and organization-owned drones
      const adnAssigned = response.data?.adnAssigned || [];
      const orgOwned = response.data?.organizationOwned || [];

      this.assignedDrones = [...adnAssigned, ...orgOwned];
    } catch (error: any) {
      console.error('Failed to load assigned drones:', error);
      this.assignedDrones = [];
    }
  }

  toggleDroneSelection(droneId: string): void {
    const index = this.selectedDroneIds.indexOf(droneId);
    if (index > -1) {
      this.selectedDroneIds.splice(index, 1);
    } else {
      this.selectedDroneIds.push(droneId);
    }
  }

  isDroneSelected(droneId: string): boolean {
    return this.selectedDroneIds.includes(droneId);
  }

  get filteredDrones(): Drone[] {
    if (!this.droneSearchQuery.trim()) {
      return this.availableDrones;
    }

    const query = this.droneSearchQuery.toLowerCase();
    return this.availableDrones.filter(drone =>
      drone.serialNumber?.toLowerCase().includes(query) ||
      drone.nickname?.toLowerCase().includes(query) ||
      drone.model?.toLowerCase().includes(query)
    );
  }

  async assignSelectedDrones(): Promise<void> {
    if (this.selectedDroneIds.length === 0) return;

    this.saving = true;
    this.errorMessage = '';
    this.successMessage = '';

    try {
      for (const droneId of this.selectedDroneIds) {
        await firstValueFrom(
          this.http.post(
            `${environment.apiUrl}/admin/organizations/${this.organizationId}/drones`,
            { droneId }
          )
        );
      }

      this.successMessage = `${this.selectedDroneIds.length} drone(s) assigned successfully`;
      this.selectedDroneIds = [];

      // Reload available drones
      await this.loadAvailableDrones();

      setTimeout(() => this.successMessage = '', 3000);
    } catch (error: any) {
      console.error('Failed to assign drones:', error);
      this.errorMessage = 'Failed to assign drones. Please try again.';
    } finally {
      this.saving = false;
    }
  }

  // =========================================
  // Step 3: Team Members
  // =========================================

  async loadPendingInvitations(): Promise<void> {
    try {
      const response = await firstValueFrom(
        this.http.get<any>(
          `${environment.apiUrl}/admin/organizations/${this.organizationId}/invitations?status=pending`
        )
      );

      this.pendingInvitations = response.data?.invitations || [];
    } catch (error: any) {
      console.error('Failed to load invitations:', error);
    }
  }

  async loadExistingMembers(): Promise<void> {
    this.loadingMembers = true;

    try {
      const response = await firstValueFrom(
        this.http.get<any>(
          `${environment.apiUrl}/admin/organizations/${this.organizationId}/memberships?status=active`
        )
      );

      this.existingMembers = response.data?.members || [];
    } catch (error: any) {
      console.error('Failed to load existing members:', error);
      this.existingMembers = [];
    } finally {
      this.loadingMembers = false;
    }
  }

  async addTeamMember(): Promise<void> {
    if (this.teamMemberForm.invalid) {
      this.teamMemberForm.markAllAsTouched();
      return;
    }

    this.addingMember = true;
    this.errorMessage = '';

    try {
      const formData = this.teamMemberForm.value;

      await firstValueFrom(
        this.http.post(
          `${environment.apiUrl}/admin/organization-invitations`,
          {
            organizationId: this.organizationId,
            email: formData.email,
            firstName: formData.firstName || undefined,
            lastName: formData.lastName || undefined,
            role: formData.role,
            skipEmail: true, // Don't send email automatically
          }
        )
      );

      this.successMessage = 'Team member invitation created';
      this.teamMemberForm.reset({ role: MembershipRoles.Pilot });

      // Reload invitations
      await this.loadPendingInvitations();

      setTimeout(() => this.successMessage = '', 3000);
    } catch (error: any) {
      console.error('Failed to add team member:', error);
      if (error.status === 409) {
        this.errorMessage = 'A user with this email already exists or has a pending invitation';
      } else {
        this.errorMessage = 'Failed to add team member. Please try again.';
      }
    } finally {
      this.addingMember = false;
    }
  }

  async sendInvitationEmail(invitationId: string): Promise<void> {
    try {
      await firstValueFrom(
        this.http.post(
          `${environment.apiUrl}/admin/organization-invitations/${invitationId}/send-email`,
          {}
        )
      );

      this.successMessage = 'Invitation email sent';

      // Reload invitations to update status
      await this.loadPendingInvitations();

      setTimeout(() => this.successMessage = '', 3000);
    } catch (error: any) {
      console.error('Failed to send invitation email:', error);
      this.errorMessage = 'Failed to send invitation email';
    }
  }

  // =========================================
  // Helper Methods
  // =========================================

  getRoleLabel(role: number): string {
    const labels: string[] = [];

    if (role & MembershipRoles.Owner) labels.push('Owner');
    if (role & MembershipRoles.ChiefSupervisor) labels.push('Chief Supervisor');
    if (role & MembershipRoles.PilotInCharge) labels.push('PIC');
    if (role & MembershipRoles.Pilot) labels.push('Pilot');

    return labels.join(', ') || 'None';
  }

  /** Check if a role includes PIC bit */
  hasRolePIC(role: number): boolean {
    return (role & MembershipRoles.PilotInCharge) !== 0;
  }

  /** Check if a role includes Pilot bit */
  hasRolePilot(role: number): boolean {
    return (role & MembershipRoles.Pilot) !== 0;
  }

  getEmailStatusBadgeClass(status: string): string {
    switch (status) {
      case 'sent':
        return 'bg-success';
      case 'failed':
        return 'bg-danger';
      case 'never_sent':
      default:
        return 'bg-secondary';
    }
  }

  getEmailStatusLabel(status: string): string {
    switch (status) {
      case 'sent':
        return 'Sent';
      case 'failed':
        return 'Failed';
      case 'never_sent':
      default:
        return 'Not Sent';
    }
  }

  formatDate(dateString: string): string {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  }
}
